import { Button, Input, List, Modal, Message, Space, Tag, Typography } from "@arco-design/web-react";
import { RefInputType } from "@arco-design/web-react/lib/Input";
import { User } from "@sendbird/chat/lib/__definition";
import { useSendbirdStateContext } from "@sendbird/uikit-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { RootState } from "../../../../store/store";
import { getButtonColor, getButtonTextColor } from "../../../../utils/cssVariables";
import { MemberWithMetaData } from "../../types";
import {
  ensureSendbirdUsers,
  searchPlatformUsers,
} from "../../utils/platformUserSearch";

import "../StartGroupChatDialog/StartGroupChatDialog.css";

type Props = {
  visible: boolean;
  channelUrl: string;
  existingMemberIds: string[];
  onClose: () => void;
  onMembersAdded: () => void;
};

export function AddMembersDialog({
  visible,
  channelUrl,
  existingMemberIds,
  onClose,
  onMembersAdded,
}: Props) {
  const { t } = useTranslation();
  const userInputRef = useRef<RefInputType>(null);
  const currentUserID = useSelector((state: RootState) => state.user.user?.id);
  const [usersList, setUsersList] = useState<User[]>([]);
  const [showedUsersList, setShowedUsersList] = useState<User[]>([]);
  const [selectedUsersList, setSelectedUsersList] = useState<User[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<string[]>([]);
  const [filter, setFilter] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    stores: {
      sdkStore: { sdk },
    },
  } = useSendbirdStateContext();

  const existingMemberSet = new Set(existingMemberIds);

  const getShowedUsers = (list: User[]) =>
    list
      .filter((user) => {
        const isCurrentUser = user.userId === currentUserID?.toString();
        const isExistingMember = existingMemberSet.has(user.userId);
        const isSelectedUser = selectedUsersList.some(
          (selectedUser) => selectedUser.userId === user.userId
        );
        return !isCurrentUser && !isExistingMember && !isSelectedUser;
      })
      .slice(0, 5);

  useEffect(() => {
    const fetchData = async () => {
      const matchedUsers = await searchPlatformUsers(filter);

      if (matchedUsers.length) {
        setUsersList(matchedUsers);
        setShowedUsersList(getShowedUsers(matchedUsers));
      } else {
        setUsersList([]);
        setShowedUsersList([]);
      }
    };

    if (visible && filter) {
      fetchData().catch(console.error);
    } else {
      setUsersList([]);
      setShowedUsersList([]);
    }
  }, [filter, visible, existingMemberIds.join(",")]);

  useEffect(() => {
    const fetchBlocked = async () => {
      const bUsersQuery = sdk.createBlockedUserListQuery?.();
      const blockedUsersData: User[] = await bUsersQuery?.next();
      if (blockedUsersData?.length) {
        setBlockedUserIds(blockedUsersData.map((user) => user.userId));
      }
    };

    if (visible) {
      fetchBlocked().catch(console.error);
    }
  }, [visible, sdk]);

  useEffect(() => {
    setShowedUsersList(getShowedUsers(usersList));
  }, [selectedUsersList.length, usersList]);

  const handleSelectUser = (user: User) => {
    setSelectedUsersList((prevState) => [...prevState, user]);
    userInputRef.current?.focus();
    setFilter("");
  };

  const handleRemoveUser = (user: User) => {
    setSelectedUsersList((prevState) =>
      prevState.filter((selectedUser) => selectedUser.userId !== user.userId)
    );
  };

  const handleUnblockAndSelect = (user: User) => {
    sdk.unblockUserWithUserId(user.userId).then(() => {
      handleSelectUser(user);
    });
  };

  const resetForm = () => {
    setFilter("");
    setSelectedUsersList([]);
    setShowedUsersList([]);
    setUsersList([]);
  };

  const handleAddMembers = async () => {
    if (!channelUrl || selectedUsersList.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const userIds = selectedUsersList.map((user) => user.userId);
      await ensureSendbirdUsers(userIds);
      const channel = await sdk.groupChannel.getChannel(channelUrl);
      await channel.inviteWithUserIds(userIds);
      onMembersAdded();
      onClose();
      resetForm();
    } catch {
      Message.error({
        content: t("messages.add_members_error"),
        closable: true,
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderUser = (item: MemberWithMetaData) => {
    const isBlocked = blockedUserIds.includes(item.userId);

    return (
      <List.Item
        key={item.userId}
        actions={[
          <Button
            style={{
              color: getButtonTextColor(),
              backgroundColor: getButtonColor(),
            }}
            size="mini"
            shape="round"
            onClick={() =>
              isBlocked ? handleUnblockAndSelect(item) : handleSelectUser(item)
            }
          >
            {isBlocked
              ? t("messages.unblock_and_start_messaging")
              : t("messages.add_member_select_button")}
          </Button>,
        ]}
      >
        <List.Item.Meta
          title={
            <div>
              {item.nickname}
              {isBlocked && (
                <span className="start-conversation-dialog__blocked-user">
                  {t("messages.is_blocked")}
                </span>
              )}
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <Modal
      title={t("messages.add_members_modal_title")}
      visible={visible}
      onCancel={() => {
        onClose();
        resetForm();
      }}
      autoFocus
      focusLock
      className="start-group-chat-dialog"
      footer={null}
    >
      <div className="start-group-chat-dialog__content">
        {selectedUsersList.length > 0 ? (
          <Space size="mini" wrap>
            {selectedUsersList.map((user) => (
              <Tag
                key={user.userId}
                closable
                onClose={() => handleRemoveUser(user)}
              >
                {user.nickname}
              </Tag>
            ))}
          </Space>
        ) : (
          <div className="start-group-chat-dialog__selected-users-placeholder">
            <Typography.Text type="secondary">
              {t("messages.add_members_list_placeholder")}
            </Typography.Text>
          </div>
        )}

        <Input
          allowClear
          value={filter}
          onChange={setFilter}
          placeholder={t("messages.user_search_placeholder")}
          ref={userInputRef}
        />
        <List
          className="list-demo-actions"
          size="small"
          dataSource={showedUsersList}
          render={renderUser}
          noDataElement={
            <div className="start-group-chat-dialog__no-users-message">
              {t("messages.find_users_placeholder")}
            </div>
          }
        />
        <Button
          className="start-group-chat-dialog__button"
          shape="round"
          loading={isSubmitting}
          onClick={handleAddMembers}
          disabled={selectedUsersList.length === 0}
        >
          {t("messages.add_members_button")}
        </Button>
      </div>
    </Modal>
  );
}
